import React, { useState, useEffect } from 'react';
import { Building, Home, Building2, Hotel, ArrowRight, Wallet, LayoutGrid, BrainCircuit, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { propertyTypes } from '../data/mock';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import { track } from '../services/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Building: Building,
  Home: Home,
  Building2: Building2,
  Hotel: Hotel
};

// Property type localisation mapping
const propTypeKeyMap = {
  'Co-Living': 'prop.coliving',
  'Hostel/PG': 'prop.hostel',
  'Flat': 'prop.flat',
  'Studio': 'prop.studio'
};

const HeroSection = () => {
  const { t } = useLanguage();
  const [selectedType, setSelectedType] = useState('Co-Living');
  const [mobile, setMobile] = useState('');
  const [loading, setLoading] = useState(false);
  const [wordIdx, setWordIdx] = useState(0);
  const [fading, setFading] = useState(false);
  const [propIdx, setPropIdx] = useState(0);
  const [propFading, setPropFading] = useState(false);

  const rotatingWords = t('hero.rotating_words');
  const propertyWords = t('card.property_words');

  useEffect(() => {
    const interval = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setWordIdx((prev) => (prev + 1) % rotatingWords.length);
        setFading(false);
      }, 280);
    }, 2200);
    return () => clearInterval(interval);
  }, [rotatingWords.length]);

  useEffect(() => {
    const interval = setInterval(() => {
      setPropFading(true);
      setTimeout(() => {
        setPropIdx((prev) => (prev + 1) % propertyWords.length);
        setPropFading(false);
      }, 280);
    }, 1800);
    return () => clearInterval(interval);
  }, [propertyWords.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    track('cta_hero_demo_submitted', { property_type: selectedType, mobile_provided: !!mobile });
    try {
      await axios.post(`${API}/demo-requests`, {
        property_type: selectedType,
        mobile: mobile,
        source: 'homepage-hero'
      });
    } catch (_) { /* still success UX */ }
    toast({
      title: t('hero.toast_title'),
      description: t('hero.toast_desc', mobile),
      duration: 5000
    });
    setMobile('');
    setLoading(false);
  };

  return (
    <section
      data-testid="hero-section"
      className="pt-24 pb-16 bg-gradient-to-br from-purple-50 via-blue-50 to-purple-50 relative overflow-hidden"
    >
      {/* Decorative dots */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-20 left-10 w-2 h-2 bg-blue-600 rounded-full"></div>
        <div className="absolute top-40 left-20 w-2 h-2 bg-purple-600 rounded-full"></div>
        <div className="absolute top-60 left-32 w-2 h-2 bg-blue-600 rounded-full"></div>
        <div className="absolute top-32 right-40 w-2 h-2 bg-purple-600 rounded-full"></div>
        <div className="absolute top-52 right-20 w-2 h-2 bg-blue-600 rounded-full"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* LEFT */}
          <div>
            <div className="mb-4">
              <p className="text-sm font-bold text-blue-600 tracking-wider mb-2">{t('hero.brand')}</p>
              <div className="inline-block bg-gradient-to-r from-orange-100 to-pink-100 border border-orange-200 text-orange-700 px-3 py-1 rounded-full text-xs font-semibold tracking-wider">
                {t('hero.badge')}
              </div>
            </div>

            {/* H1 — 3 lines: line1 / line2 / rotating-word */}
            <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="block">{t('hero.h1_line1')}</span>
              <span className="block">{t('hero.h1_line2')}</span>
              <span className="block">
                <span
                  data-testid="rotating-word"
                  className={`text-green-500 inline-block transition-all duration-300 ${
                    fading ? 'opacity-0 -translate-y-1' : 'opacity-100 translate-y-0'
                  }`}
                >
                  {rotatingWords[wordIdx]}
                </span>
                {t('hero.h1_trail')}
              </span>
            </h1>

            <p className="text-xl text-gray-700 mb-3">{t('hero.subtext')}</p>
            <p className="text-2xl font-semibold text-green-500 mb-8">{t('hero.tagline')}</p>

            {/* Property type picker */}
            <div className="mb-6">
              <p className="text-gray-700 font-medium mb-3">{t('hero.i_manage')}</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {propertyTypes.map((type) => {
                  const Icon = iconMap[type.icon] || Building;
                  const label = t(propTypeKeyMap[type.name] || '') || type.name;
                  return (
                    <button
                      key={type.id}
                      onClick={() => { setSelectedType(type.name); track('cta_property_type_selected', { property_type: type.name, surface: 'hero' }); }}
                      className={`p-4 rounded-xl border-2 transition-all hover:shadow-md ${
                        selectedType === type.name
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 bg-white hover:border-blue-300'
                      }`}
                      data-testid={`hero-property-type-${type.name}`}
                    >
                      <Icon className={`w-8 h-8 mx-auto mb-2 ${selectedType === type.name ? 'text-blue-600' : 'text-gray-600'}`} />
                      <p className="text-sm font-medium text-gray-700">{label}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Demo form */}
            <form onSubmit={handleSubmit} className="flex gap-2 max-w-md" data-testid="hero-demo-form">
              <Input
                type="tel"
                placeholder={t('hero.mobile_placeholder')}
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                className="flex-1 rounded-lg border-2 border-gray-300 focus:border-blue-600"
                required
                data-testid="hero-mobile-input"
              />
              <Button
                type="submit"
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-6 flex items-center gap-2 disabled:opacity-50"
                data-testid="hero-demo-submit"
              >
                {loading ? t('hero.submit_loading') : t('hero.submit_idle')}
                {!loading && <ArrowRight size={18} />}
              </Button>
            </form>
          </div>

          {/* RIGHT — preview card */}
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-blue-200/40 via-purple-200/30 to-pink-200/40 rounded-[32px] blur-2xl"></div>

            <div
              data-testid="hero-preview-card"
              className="relative bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden"
            >
              {/* Browser chrome */}
              <div className="flex items-center gap-1.5 px-5 py-3 border-b border-gray-100 bg-gray-50">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400"></span>
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-400"></span>
                <span className="w-2.5 h-2.5 rounded-full bg-green-400"></span>
                <div className="flex-1 text-center text-[11px] text-gray-400 font-mono">
                  {t('card.url')}
                </div>
              </div>

              {/* Hero band */}
              <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-blue-200 mb-1">
                      {t('card.kicker')}
                    </p>
                    <h3 className="text-2xl font-bold leading-tight">{t('card.kicker_h')}</h3>
                  </div>
                  <div className="hidden sm:flex items-center gap-1 bg-green-500/90 px-2 py-1 rounded-full text-[10px] font-bold">
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                    {t('card.live')}
                  </div>
                </div>
              </div>

              {/* Stat rail */}
              <div className="grid grid-cols-3 divide-x divide-gray-100 border-b border-gray-100">
                <div className="p-4 text-center">
                  <p className="text-[10px] uppercase text-gray-500 font-semibold tracking-wider">{t('card.stat.collected')}</p>
                  <p className="text-lg font-bold text-gray-900 mt-0.5">₹2.4L</p>
                </div>
                <div className="p-4 text-center">
                  <p className="text-[10px] uppercase text-gray-500 font-semibold tracking-wider">{t('card.stat.due')}</p>
                  <p className="text-lg font-bold text-orange-600 mt-0.5">₹38K</p>
                </div>
                <div className="p-4 text-center">
                  <p className="text-[10px] uppercase text-gray-500 font-semibold tracking-wider">{t('card.stat.occupancy')}</p>
                  <p className="text-lg font-bold text-emerald-600 mt-0.5">92%</p>
                </div>
              </div>

              {/* Feature rows */}
              <div className="p-5 space-y-3">
                <FeatureRow icon={Wallet} color="emerald" title={t('card.f1.title')} sub={t('card.f1.sub')} />
                <FeatureRow icon={LayoutGrid} color="blue" title={t('card.f2.title')} sub={t('card.f2.sub')} />
                <FeatureRow icon={BrainCircuit} color="purple" title={t('card.f3.title')} sub={t('card.f3.sub')} aiBadge />
              </div>

              {/* Bharat strip with rotating property word — no trailing gap */}
              <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 bg-gradient-to-r from-orange-50 via-amber-50 to-emerald-50">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 via-white to-emerald-500 flex items-center justify-center flex-shrink-0 border border-gray-200">
                    <span className="text-[13px]">🇮🇳</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-widest text-orange-600 font-bold">
                      {t('card.bharat_kicker')}
                    </p>
                    <p className="text-sm font-bold text-gray-900 whitespace-nowrap">
                      {t('card.bharat_title_prefix')}{' '}
                      <span
                        data-testid="hero-property-rotator"
                        className={`text-blue-600 transition-all duration-300 ${
                          propFading ? 'opacity-0 -translate-y-1' : 'opacity-100 translate-y-0'
                        }`}
                      >
                        {propertyWords[propIdx]}
                      </span>{' '}
                      {t('card.bharat_title_suffix')}
                    </p>
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-1 text-[11px] text-emerald-700 font-semibold flex-shrink-0 ml-2">
                  <Check size={12} /> {t('card.secure')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const colorMap = {
  emerald: 'bg-emerald-100 text-emerald-600',
  blue: 'bg-blue-100 text-blue-600',
  purple: 'bg-purple-100 text-purple-600'
};

const FeatureRow = ({ icon: Icon, color, title, sub, aiBadge }) => (
  <div className="flex items-start gap-3">
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${colorMap[color]}`}>
      <Icon size={18} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="text-sm font-semibold text-gray-900 leading-tight">{title}</p>
        {aiBadge && (
          <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white">
            AI
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500 mt-0.5">{sub}</p>
    </div>
  </div>
);

export default HeroSection;
